import React, { Children, CSSProperties, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { DateTime } from 'luxon';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { isLatLngLiteral } from '@googlemaps/typescript-guards';
import { createCustomEqual, deepEqual } from 'fast-equals';
import {
    Box, Button, Checkbox, DialogActions, DialogContent, FormControlLabel, Grid, IconButton, TextField,
} from '@mui/material';
import { Delete, Visibility } from '@mui/icons-material';
import DropCustom from 'components/dropZone/DropCustom';
import { ModalActionProps } from 'models/AutoModals';
import { YardModel } from 'models/YardModel';
import ViewYardInstructions from 'modules/yards/common/ViewYardInstructions';
import auth from 'recoil/atoms/auth';


const mapStatus = (status: Status) => {
    return <h1>{status}</h1>;
};

type YardDialogProps = {
    yard?: YardModel,
    create?: boolean,
    isLoading?: boolean,
} & ModalActionProps<YardModel>

const YardDialog = ({create, onFinish, yard, isLoading}: YardDialogProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const [coordinates, setCoordinates] = useState<google.maps.LatLngLiteral>();
    const [center, setCenter] = useState<google.maps.LatLngLiteral>();
    const [instructionsFile, setInstructionsFile] = useState<File | undefined>();
    const [clearInstructions, setClearInstructions] = useState(0);

    useEffect(() => {
        let temp = yard?.coordinates ? {
            lat: yard.coordinates.latitude,
            lng: yard.coordinates.longitude,
        } : undefined

        setCoordinates(temp);
        setCenter(temp || (isLoading ? {
            lat: 36.65,
            lng: 137.00,
        } : {
            lat: 43.12,
            lng: 132.00,
        }))
    }, [isLoading, yard?.coordinates]);

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const data = Object.fromEntries(new FormData(event.currentTarget).entries()) as unknown as YardModel;

        onFinish({
            ...yard, // TODO check if it still need
            ...data,
            coordinates: {
                latitude: coordinates!.lat,
                longitude: coordinates!.lng,
            },
            instructions: clearInstructions ? false : (Boolean(yard?.instructions)),
            instructions_file: instructionsFile,
            instructionsClear: clearInstructions,
            isDock: data.isDock ? 1 : 0,
            deletedAt: data.deletedAt,
        });
    };

    const clickMap = (e: any) => {
        const literal = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
        };

        setCoordinates(literal);
        setCenter(literal);
    };

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item sm={6}>
                        <TextField
                            autoFocus
                            fullWidth
                            required
                            id="name"
                            name="name"
                            label={t('name')}
                            defaultValue={yard?.name}
                        />

                        <TextField
                            fullWidth
                            required
                            id="address"
                            name="address"
                            label={t('address')}
                            defaultValue={yard?.address}
                        />

                        <TextField
                            fullWidth
                            multiline
                            id="description"
                            name="description"
                            label={t('description')}
                            defaultValue={yard?.description}
                        />

                        <TextField
                            fullWidth
                            multiline
                            id="workTime"
                            name="workTime"
                            label={t('work_time')}
                            defaultValue={yard?.workTime}
                        />

                        <TextField
                            fullWidth
                            type="email"
                            id="email"
                            name="email"
                            label={t('email')}
                            defaultValue={yard?.email}
                        />

                        <TextField
                            fullWidth
                            type="tel"
                            id="phone"
                            name="phone"
                            label={t('phone')}
                            defaultValue={yard?.phone}
                        />

                        <TextField
                            fullWidth
                            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                            id="capacity"
                            name="capacity"
                            label={t('capacity')}
                            defaultValue={yard?.capacity}
                        />

                        <FormControlLabel
                            control={<Checkbox
                                name="isDock"
                                value={1}
                                defaultChecked={!!yard?.isDock}
                                color="primary"
                            />}
                            label={t('is_dock')}
                        />

                        <FormControlLabel
                            control={<Checkbox
                                name="deletedAt"
                                value={DateTime.now().toISO()}
                                defaultChecked={!!yard?.deletedAt}
                                color="primary"
                            />}
                            label={t('disabled')}
                        />
                    </Grid>

                    <Grid item sm={6}>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    id="lat"
                                    name="coordinates[lat]"
                                    label={t('latitude')}
                                    value={coordinates?.lat || ''}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    id="lng"
                                    name="coordinates[lng]"
                                    label={t('longitude')}
                                    value={coordinates?.lng || ''}
                                />
                            </Grid>
                        </Grid>

                        <Wrapper apiKey={process.env.REACT_APP_GMAP_KEY || ''} render={mapStatus}>
                            <Map
                                center={center}
                                onClick={clickMap}
                                style={{width: '100%', height: 310, marginBottom: 24}}
                            >
                                <Marker position={coordinates}/>
                            </Map>
                        </Wrapper>

                        <Grid container>
                            <Grid item xs={10}>
                                <DropCustom
                                    title={t('yard_delivery_instructions')}
                                    options={{
                                        onDropAccepted: files => setInstructionsFile(files[0]),
                                        accept: {
                                            'application/pdf': ['.pdf'],
                                        },
                                        multiple: false,
                                    }}
                                    compact
                                />
                            </Grid>
                            <Grid item xs={2} sx={{textAlign: 'right'}}>
                                {((!clearInstructions && yard?.instructions) || instructionsFile) && (
                                    <IconButton
                                        onClick={() => {
                                            setClearInstructions(1);
                                            setInstructionsFile(undefined);
                                        }}
                                    >
                                        <Delete/>
                                    </IconButton>
                                )}

                                <br/>
                                {!clearInstructions && yard?.instructions && (
                                    <IconButton>
                                        <ViewYardInstructions
                                            yard={yard}
                                            icon={Visibility}
                                        />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

            </DialogContent>

            {my.permissions.has(create ? 'yards create' : 'yards update') &&
                <DialogActions>
                    <Button type="submit">{create ? 'Add' : 'Save'}</Button>
                </DialogActions>
            }
        </Box>
    );
};


type MapProps = PropsWithChildren<{
    onClick: (e: any) => void,
    style: CSSProperties,
}> & google.maps.MapOptions

const Map = ({onClick, children, style, ...options}: MapProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<google.maps.Map>();

    useEffect(() => {
        if (ref.current && !map) {
            setMap(new window.google.maps.Map(ref.current, {
                zoom: 8,
                disableDefaultUI: true,
            }));

        }
    }, [ref, map]);

    // because React does not do deep comparisons, a custom hook is used
    // see discussion in https://github.com/googlemaps/js-samples/issues/946
    useDeepCompareEffectForMaps(() => {
        if (map) {
            map.setOptions(options);
        }
    }, [map, options]);

    useEffect(() => {
        if (map) {
            google.maps.event.clearListeners(map, 'click')
            map.addListener('click', onClick);
        }
    }, [map, onClick]);

    return (
        <>
            <div ref={ref} style={style}/>
            {Children.map(children, (child) => {
                if (React.isValidElement(child)) {
                    // set the map prop on the child component
                    // @ts-ignore
                    return React.cloneElement(child, {map});
                }
            })}
        </>
    )
};

type MarkerProps = {
    position: google.maps.LatLng | google.maps.LatLngLiteral | null | undefined,
}

const Marker = (options: MarkerProps) => {
    const [marker, setMarker] = useState<google.maps.Marker>();

    useEffect(() => {
        if (!marker) {
            setMarker(new google.maps.Marker());
        }

        return () => {
            if (marker) {
                marker.setMap(null);
            }
        };
    }, [marker]);

    useEffect(() => {
        if (marker) {
            marker.setOptions(options);
        }
    }, [marker, options]);

    return null;
};

const deepCompareEqualsForMaps = createCustomEqual({
    createCustomConfig: () => ({
        areObjectsEqual: (a, b) => {
            if (
                isLatLngLiteral(a) ||
                a instanceof google.maps.LatLng ||
                isLatLngLiteral(b) ||
                b instanceof google.maps.LatLng
            ) {
                return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
            }

            // use fast-equals for other objects
            return deepEqual(a, b);
        }
    })
});

function useDeepCompareMemoize(value: any) {
    const ref = useRef();

    if (!deepCompareEqualsForMaps(value, ref.current)) {
        ref.current = value;
    }

    return ref.current;
}

function useDeepCompareEffectForMaps(callback: any, dependencies: any) {
    useEffect(callback, dependencies.map(useDeepCompareMemoize));
}


export default YardDialog;
